import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import {
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { When } from 'react-if';

import Loading from '../../loading';
import NoData from '../../no-data';
import { UserService } from '../../../services/user.service';
import { UserTypes } from '../../../types';
import { PaginatorFactory } from '../../../factory';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import { UnifiedFilterBar, FilterField } from '../../shared/UnifiedFilterBar';

const COLUMNS = [
    { field: 'firstName', headerName: 'Name', sortable: true },
    { field: 'employeeId', headerName: 'Employee ID', sortable: true },
    { field: 'deprovisionSnapshot.groupNames', headerName: 'Group', sortable: false },
    { field: 'deprovisionSnapshot.teamName', headerName: 'Team', sortable: true },
    { field: 'deprovisionSnapshot.tagNames', headerName: 'Tags', sortable: false },
    // Singular label: users carry one device. The snapshot field stays an
    // array defensively, so display still joins just in case.
    { field: 'deprovisionSnapshot.deviceSerials', headerName: 'Device Serial', sortable: false },
    { field: 'deletedAt', headerName: 'Deprovisioned', sortable: true },
];

const PRIMARY_FILTERS: FilterField[] = [
    { key: 'name', label: 'Name', type: 'text', placeholder: 'Search by name...' },
    { key: 'employeeId', label: 'Employee ID', type: 'text', placeholder: 'Search by ID...' },
];

const TIME_PERIOD_LABELS: Record<string, string> = {
    '7d': 'Last 7 Days',
    '14d': 'Last 14 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
};

const VALID_PERIODS = new Set(['', '7d', '14d', '30d', '90d', 'custom']);

interface PeriodState {
    period: string;
    start: Dayjs | null;
    end: Dayjs | null;
}

const EMPTY_PERIOD: PeriodState = { period: '', start: null, end: null };

// Day-bounded ISO range in the viewer's timezone, applied to deletedAt.
function resolveDateRange(p: PeriodState): { fromDate: string; toDate: string } | null {
    if (p.period !== 'custom') {
        const days = p.period === '7d' ? 7 : p.period === '14d' ? 14 : p.period === '30d' ? 30 : p.period === '90d' ? 90 : null;
        if (days === null) return null;
        return {
            fromDate: dayjs().subtract(days, 'day').startOf('day').toISOString(),
            toDate: dayjs().endOf('day').toISOString(),
        };
    }
    if (!p.start || !p.end || !p.start.isValid() || !p.end.isValid()) return null;
    return {
        fromDate: p.start.startOf('day').toISOString(),
        toDate: p.end.endOf('day').toISOString(),
    };
}

type FilterValues = { name: string; employeeId: string };
const EMPTY_VALUES: FilterValues = { name: '', employeeId: '' };

type ApiFilters = FilterValues & { fromDate?: string; toDate?: string };

const displayName = (u: UserTypes.Model): string => {
    const fromDoc = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    if (fromDoc) return fromDoc;
    const s = u.deprovisionSnapshot;
    return `${s?.firstName ?? ''} ${s?.lastName ?? ''}`.trim() || 'N/A';
};

export const DeprovisionedUsersList: React.FC = () => {
    const [items, setItems] = useState<UserTypes.Model[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 0, perPage: 25 });
    const [sort, setSort] = useState<PaginatorFactory<any>['sort']>({ deletedAt: 'desc' });
    const [apiFilters, setApiFilters] = useState<ApiFilters>(EMPTY_VALUES);

    const [values, setValues] = useState<FilterValues>(EMPTY_VALUES);
    const [applied, setApplied] = useState<FilterValues>(EMPTY_VALUES);
    const [periodState, setPeriodState] = useState<PeriodState>(EMPTY_PERIOD);
    const [appliedPeriod, setAppliedPeriod] = useState<PeriodState>(EMPTY_PERIOD);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        UserService.fetchDeprovisioned({
            page: pagination.page + 1,
            perPage: pagination.perPage,
            filter: apiFilters,
            sort,
        })
            .then((res) => {
                if (cancelled) return;
                setItems(res.items ?? []);
                setTotal(res.total ?? 0);
            })
            .catch((err) => {
                console.error('[DeprovisionedUsersList] fetch failed', err);
                if (!cancelled) toast('Failed to load deprovisioned users.', { type: 'error', autoClose: 2500 });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [pagination, sort, apiFilters]);

    const dispatchFilters = useCallback((next: FilterValues, nextPeriod: PeriodState) => {
        const range = resolveDateRange(nextPeriod);
        setApiFilters({ ...next, ...(range ?? {}) });
        setPagination((p) => ({ ...p, page: 0 }));
    }, []);

    const handleApply = useCallback(() => {
        if (periodState.period === 'custom' && (!periodState.start || !periodState.end)) {
            toast('Pick a start and end date for the custom range.', { type: 'warning', autoClose: 2500 });
            return;
        }
        setApplied(values);
        setAppliedPeriod(periodState);
        dispatchFilters(values, periodState);
    }, [values, periodState, dispatchFilters]);

    const handleClear = useCallback(() => {
        setValues(EMPTY_VALUES);
        setApplied(EMPTY_VALUES);
        setPeriodState(EMPTY_PERIOD);
        setAppliedPeriod(EMPTY_PERIOD);
        dispatchFilters(EMPTY_VALUES, EMPTY_PERIOD);
    }, [dispatchFilters]);

    const setTimePeriod = useCallback((next: string) => {
        if (!VALID_PERIODS.has(next)) return;
        setPeriodState((prev) => (next !== 'custom' ? { period: next, start: null, end: null } : { ...prev, period: next }));
    }, []);

    const periodPillLabel = (p: PeriodState): string | null => {
        if (!p.period) return null;
        if (p.period === 'custom') {
            if (!p.start || !p.end) return null;
            const sameYear = p.start.year() === p.end.year();
            const startFmt = sameYear ? p.start.format('MMM D') : p.start.format('MMM D, YYYY');
            return `${startFmt} – ${p.end.format('MMM D, YYYY')}`;
        }
        return TIME_PERIOD_LABELS[p.period] ?? p.period;
    };

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        const removeApplied = (next: FilterValues) => {
            setValues(next);
            setApplied(next);
            dispatchFilters(next, appliedPeriod);
        };
        if (applied.name) pills.push({
            key: 'name', label: `Name: ${applied.name}`,
            onRemove: () => removeApplied({ ...applied, name: '' }),
        });
        if (applied.employeeId) pills.push({
            key: 'employeeId', label: `Employee ID: ${applied.employeeId}`,
            onRemove: () => removeApplied({ ...applied, employeeId: '' }),
        });
        const pLabel = periodPillLabel(appliedPeriod);
        if (pLabel) {
            pills.push({
                key: 'time-period',
                label: `Deprovisioned: ${pLabel}`,
                onRemove: () => {
                    setPeriodState(EMPTY_PERIOD);
                    setAppliedPeriod(EMPTY_PERIOD);
                    dispatchFilters(applied, EMPTY_PERIOD);
                },
            });
        }
        return pills;
    }, [applied, appliedPeriod, dispatchFilters]);

    const makeCsvDownload = async () => {
        const res = await UserService.fetchDeprovisioned({
            page: 1,
            perPage: 100000,
            filter: apiFilters,
            sort,
        });

        // Tag columns follow the tagOne/tagTwo trailing-column convention of
        // the POM Users / Alerts exports so external parsers stay aligned.
        const tagLists = (res.items ?? []).map((u) => u.deprovisionSnapshot?.tagNames ?? []);
        const maxTags = tagLists.reduce((m, list) => Math.max(m, list.length), 0);
        const TAG_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
        const tagColKey = (i: number) => `tag${TAG_WORDS[i] ?? i + 1}`;

        const csvArray = (res.items ?? []).map((u, idx) => {
            const s = u.deprovisionSnapshot;
            const tagCols: Record<string, string> = {};
            for (let i = 0; i < maxTags; i++) {
                tagCols[tagColKey(i)] = tagLists[idx][i] ?? '';
            }
            return {
                userName: displayName(u),
                employeeId: u.employeeId ?? s?.employeeId ?? '',
                groupName: (s?.groupNames ?? []).join('; '),
                teamName: s?.teamName ?? '',
                deviceSerial: (s?.deviceSerials ?? []).join('; '),
                deprovisionedAt: u.deletedAt ? dayjs(u.deletedAt).format('MM/DD/YYYY hh:mm A') : '',
                ...tagCols,
            };
        });

        downloadCsv(csvArray, 'deprovisioned-users');
    };

    return (
        <Card sx={{ mt: 2 }}>
            <UnifiedFilterBar
                primaryFilters={PRIMARY_FILTERS}
                values={values}
                appliedFilters={appliedFilters}
                onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
                onApply={handleApply}
                onClear={handleClear}
                onExport={makeCsvDownload}
                showTimePeriod
                timePeriod={periodState.period}
                onTimePeriodChange={setTimePeriod}
                customStartDate={periodState.start}
                customEndDate={periodState.end}
                onCustomStartDateChange={(d) => setPeriodState((prev) => ({ ...prev, start: d }))}
                onCustomEndDateChange={(d) => setPeriodState((prev) => ({ ...prev, end: d }))}
            />
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((column) => (
                                <TableCell key={column.field} sortDirection={sort![column.field] || undefined}>
                                    {column.sortable ? (
                                        <TableSortLabel
                                            active={!!sort![column.field]}
                                            direction={sort![column.field]}
                                            onClick={() => setSort((s) => ({
                                                [column.field]: s![column.field] === 'asc' ? 'desc' : 'asc',
                                            }))}
                                        >
                                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                {column.headerName}
                                            </Typography>
                                        </TableSortLabel>
                                    ) : (
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                            {column.headerName}
                                        </Typography>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && items.map((u) => {
                            const s = u.deprovisionSnapshot;
                            return (
                                <TableRow key={u.id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {displayName(u)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {u.employeeId ?? s?.employeeId ?? 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {(s?.groupNames ?? []).join(', ') || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {s?.teamName ?? 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {(s?.tagNames ?? []).join(', ') || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {(s?.deviceSerials ?? []).join(', ') || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {u.deletedAt ? dayjs(u.deletedAt).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                <When condition={loading}>
                    <Loading />
                </When>

                <When condition={!loading && items.length < 1}>
                    <NoData
                        buttonText={null}
                        onClick={() => {}}
                        title={'No records found'}
                        description={'No deprovisioned users match the current filters.'}
                    />
                </When>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={pagination.perPage}
                page={pagination.page}
                onPageChange={(e, p) => setPagination((pag) => ({ ...pag, page: p }))}
                onRowsPerPageChange={(e) => setPagination((pag) => ({ ...pag, perPage: parseInt(e.target.value, 10), page: 0 }))}
            />
        </Card>
    );
};
